/**
 * Format duration from total seconds to human-readable format
 * @param totalSeconds - Duration in seconds
 * @returns Formatted duration like "3h 6m" or "30s"
 *
 * @example
 * formatDurationSeconds(3660) // "1h 1m"
 * formatDurationSeconds(90)   // "1m 30s"
 * formatDurationSeconds(45)   // "45s"
 */
const formatDurationSeconds = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60

  const parts: string[] = []
  if (hours > 0) {
    parts.push(`${hours}h`)
    if (minutes > 0) parts.push(`${minutes}m`)
  } else {
    if (minutes > 0) parts.push(`${minutes}m`)
    if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`)
  }

  return parts.join(' ')
}

/**
 * Format a duration string from minutes format to human-readable format
 * @param duration - Duration in format "186.1m" (minutes with decimal)
 * @returns Formatted duration like "3h 6m"; an em-dash when the value is missing or empty,
 *          and the input verbatim when it doesn't match the expected minutes format
 *
 * @example
 * formatDuration("186.1m") // "3h 6m"
 * formatDuration("30.5m")  // "30m 30s"
 * formatDuration(undefined) // "—"
 * formatDuration("later")   // "later"
 */
export const formatDuration = (duration: string | undefined): string => {
  if (!duration) return '—'

  // Parse format like "186.1m" or "30.5m"
  const match = duration.match(/^([\d.]+)m$/)
  if (!match) return duration // Return as-is if unexpected format

  const totalMinutes = parseFloat(match[1])
  const totalSeconds = Math.floor(totalMinutes * 60)

  return formatDurationSeconds(totalSeconds)
}

/**
 * Format a duration from milliseconds to human-readable format
 * @param durationMs - Duration in milliseconds
 * @returns Formatted duration like "1h 30m" or "45s"
 *
 * @example
 * formatDurationMs(3660000) // "1h 1m"
 * formatDurationMs(90000)   // "1m 30s"
 * formatDurationMs(45000)   // "45s"
 */
export const formatDurationMs = (durationMs: number): string => {
  const totalSeconds = Math.floor(durationMs / 1000)
  return formatDurationSeconds(totalSeconds)
}

/**
 * Format a wall-clock duration between two ISO timestamps to human-readable format.
 *
 * Prefer this over {@link formatDurationMs} on Playwright durations. Playwright's
 * reported `duration_ms` / `total_duration_ms` is slot-based: it excludes time spent
 * in worker-scoped fixtures, fixtures with a custom `timeout`, and `beforeAll`/`afterAll`
 * hooks. For Tattle specs (merchant/order/survey fixtures all set a custom timeout) that
 * hides most of the real time — a 10-minute test can read as a few seconds. The
 * start/finish timestamps bracket the whole run including fixture setup, so their
 * difference is the true wall-clock duration.
 *
 * @param startIso - ISO timestamp when the test/attempt started
 * @param endIso - ISO timestamp when the test/attempt finished
 * @returns Formatted duration like "10m 3s", or "—" if timestamps are missing/invalid
 *
 * @example
 * formatWallClockMs('2026-07-02T10:00:00Z', '2026-07-02T10:10:03Z') // "10m 3s"
 */
export const formatWallClockMs = (
  startIso: string | undefined,
  endIso: string | undefined
): string => {
  if (!startIso || !endIso) return '—'

  const start = new Date(startIso).getTime()
  const end = new Date(endIso).getTime()
  if (Number.isNaN(start) || Number.isNaN(end)) return '—'

  return formatDurationMs(Math.max(0, end - start))
}
